import { IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { fetchLocations } from '../../action/locationsCommands';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import SearchBar from '../../components/SearchBar';
import ReloadIcon from '../../components/icons/ReloadIcon';
import { makeLocationDetailsUrl } from '../../routes';
import { makePermittedLocationListSelector } from '../../selector/locationsSelector';
import LocationCard from './LocationCard';
import ObjectLimitBanner from '../../components/ObjectLimitBanner';

const LocationsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const permittedLocations = useSelector(makePermittedLocationListSelector());
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        dispatch(fetchLocations({}));
    }, []);

    const navigateToLocation = (locationId: string) => {
        navigate(makeLocationDetailsUrl(String(locationId)));
    };

    const reloadLocations = () => {
        dispatch(fetchLocations({}));
    };

    const filteredLocations =
        searchTerm.length < 3
            ? permittedLocations
            : permittedLocations.filter((location) => {
                const preparedSearchTerm = searchTerm.toLowerCase();
                return (
                    location.name
                        .toLowerCase()
                        .includes(preparedSearchTerm) ||
                    location.address.city
                        .toLowerCase()
                        .includes(preparedSearchTerm)
                );
            });

    const locationList = filteredLocations.sort((location1, location2) => {
        if (location1.name < location2.name) return -1;
        if (location1.name > location2.name) return 1;
        return 0;
    });

    return (
        <div>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>{t('locations.title')}</PageHeadline>
                    <PageSubtitle>
                        {t('locations.count', { count: locationList.length })}
                    </PageSubtitle>
                </div>
                <SearchBar
                    className={'hidden sm:block'}
                    placeholder={t('locations.search_placeholder')}
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
                <Tooltip title={'Reload'}>
                    <IconButton
                        className={'text-foreground -mr-2'}
                        onClick={reloadLocations}
                    >
                        <ReloadIcon />
                    </IconButton>
                </Tooltip>
            </PageHeader>
            <div className={'mb-4'}>
                <ObjectLimitBanner />
            </div>
            <SearchBar
                className={'w-full sm:hidden mb-5'}
                placeholder={t('locations.search_placeholder')}
                value={searchTerm}
                onChange={setSearchTerm}
            />
            <section
                className={'grid sm:grid-cols-2 max-w-full gap-y-2 gap-x-5'}
            >
                {locationList.map((location) => (
                    <LocationCard
                        onPress={() => navigateToLocation(location.id)}
                        location={location}
                    />
                ))}
            </section>
        </div>
    );
};

export default LocationsPage;
